<template>
  <div class="fabriki-page">
    <Banner
      :logo="factoryIcon"
      :image="factoryImage"
    />

    <template v-if="$viewport.isGreaterOrEquals('lg') || true">
      <div class="container">
        <CollectionGrids
          v-if="isVisibleCollection"
          :title-h1="factoryH1"
          :title="factoryShortDescription"
          :products="collections"
          :loading="isLoadingCollections"
        />

        <div
          v-if="Boolean(isVisibleCollection && isVisibleProducts)"
          class="fabriki-page__space"
        />

        <ProductsGrids
          v-if="isVisibleProducts"
          :title-h1="!isVisibleCollection ? factoryH1 : ''"
          :sections="productsSections"
          :filter="productsFilter"
          :loading="isLoadingProducts"
          :groups="characteristicGroups"
          :pagination="productsPagination"

          @change-filter="changeProductsFilter"
        />


        <SeoMicrodataCategoryProducts
          :category-name="factory.name"
          :min-price="factory?.min_price || null"
          :max-price="factory?.max_price || null"
          :description="factory.seo_description || ''"
          :count-products="factory?.product_count || 0"
          :image-id="factory?.first_product_main_photo_id || null"
        />
      </div>
    </template>
    <template v-if="!$viewport.isGreaterOrEquals('lg') && false">
      <div class="container">
        <div class="fabriki-page__mobile-content">
          <h2
            v-if="factoryShortDescription"
            v-html="factoryShortDescription"
            class="fabriki-page__mobile-title h2"
          />
        </div>
        <CatalogTemplate
          :is-component="true"
          default-base-path-api="brands"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, type Ref } from 'vue';
import { type IProduct, Product } from '#sitis/internal/controllers/products/models/Product';
import Banner from '~/components/fabriki/Banner.vue';
import ProductsGrids from '~/components/fabriki/ProductsGrids.vue';
import CollectionGrids from '~/components/fabriki/CollectionGrids.vue';
import { createError } from '#imports';
import SeoMicrodataCategoryProducts from "~/components/common/SeoMicrodataCategoryProducts.vue";

const CatalogTemplate = defineAsyncComponent(() => import("~/pages/catalog/[...slug].vue"));

export interface ICharacteristicGroup {
	id: number;
	name: string;
	sort: number;
	imageId?: number;
}
export interface IProductsFilter {
	page: number;
	sort: string | undefined;
	groupId: number | undefined;
}
export interface IProductsPagination {
	total: number;
	count: number;
}
export interface IProductsSection {
  id: number;
  sort: number;
  name: string;
  imageId?: number;

  products: IProduct[]
}

const {
  $api,
  $viewport
} = useNuxtApp();
const $route = useRoute();

const characteristicGroups: Ref<ICharacteristicGroup[]> = ref([]);
const collections: Ref<IProduct[]> = ref([]);
const isLoadingCollections: Ref<boolean> = ref(true);

const productsFilter: Ref<IProductsFilter> = ref({
  page: 1,
  sort: '',
  groupId: undefined
});
const productsPagination: Ref<IProductsPagination> = ref({ total: 0, count: 0 });
const products: Ref<IProduct[]> = ref([]);
const isLoadingProducts: Ref<boolean> = ref(true);
const productsSections: Ref<IProductsSection[]> = ref([]);

const factoryH1: ComputedRef<string> = computed(() => {
  return factory.value?.title_h1 || ''
});
const factorySlug = computed(() => $route.params.slug[0]);
const factoryIcon = computed(() => factory.value?.image_id);
const factoryImage = computed(() => {
  return factory.value?.ms_brand_page_image || factory.value?.images_ids?.[0]
});
const factoryShortDescription = computed(() => factory.value?.short_description || '');
const isVisibleCollection = computed(() => Boolean(isLoadingCollections.value || collections.value.length > 0));
const isVisibleProducts = computed(() => Boolean(isLoadingProducts.value || productsSections.value.length > 0));

const getCharacteristicGroups = async () => {
  characteristicGroups.value = await $api.agent.get(`/brands/${factorySlug.value}/characteristic-groups?filter[show-in-brand][]=1&is_product_set=0&options[all]=1`).then((res: any) => {
    const _list = (res?._data || []).filter((t: any) => !t.is_for_product_set);
    return _list.map((item: any) => ({
      id: item.id,
      name: item.name,
      sort: item.sort,
      imageId: item.image_id
    }));
  }).catch(() => []);
};

const getCollections = async () => {
  const expand = ['short_characteristics', 'promotions', 'promotions.icon'].join(', ');
  let _collections = await $api.agent.get(`/brands/${factorySlug.value}/products?per-page=100&filter[show-in-brand][]=1&is_product_set=1&expand=${expand}`).then((res: any) => res._data || []).catch(() => []);
  _collections = _collections.map((t: any) => new Product(t));
  collections.value = _collections;
  isLoadingCollections.value = false;
};

const getProducts = async () => {
  isLoadingProducts.value = true;

  const search = [
		'page=1',
		`expand=${['short_characteristics', 'characteristicGroup', 'promotions', 'promotions.icon'].join(', ')}`,
		'is_product_set=0',
    'filter[show-in-brand][]=1',
    'per-page=999'
  ];
  const res = await $api.agent.get(`/brands/${factorySlug.value}/products?${search.join('&')}`).then((res: any) => (res?._data || [])).catch(() => {
    return []
  });

  let _products = res || [];
  _products = _products.map((t: any) => new Product(t));

  let _productsSections = [...(characteristicGroups.value || [])].map((_section: any) => {
    return {
      ..._section,
      products: _products.filter((t: IProduct) => t?.characteristicGroupId === _section?.id)
    }
  });
  _productsSections = _productsSections.filter((t) => (t.products || []).length > 0);

  productsSections.value = _productsSections;
  isLoadingProducts.value = false;
};

const changeProductsFilter = async (filter: IProductsFilter) => {
  productsFilter.value = filter;
  await getProducts();
};

const getFabriky = async () => {
  const _brand = await $api.agent.get(`/brands/${factorySlug.value}`).then((res: any) => res._data).catch(() => null);
  if (!_brand) return _brand;
  const _info = await $api.agent.get(`/local-brands/${ factorySlug.value }/products`).then((res: any) => res._data).catch(() => null);
  return {
    ..._brand,
    ...(_info || {})
  };
};
const checkFabriky = () => {
  if (status.value === 'error') {
    showError({
      statusCode: 404,
      message: 'Фабрика<br/>не найдена'
    });
    return
  }
  if (status.value === 'success') {
    const websiteVisibility = factory.value?.website_visibility || [];
    if (!Boolean(websiteVisibility.includes('3'))) {
      return navigateTo('/', { redirectCode: 301 });
    }
  }
};

const { data: factory, status, execute: getFactory } = useLazyAsyncData($route.path, () => getFabriky());

watch(status, () => {
  checkFabriky();
});

useHead(() => {
  const head: any = {
    title: '',
    meta: [],
    link: []
  };

  const title = factory.value?.seo_title || factory.value?.name || '';
  if (title) {
    head.title = title;
    head.meta.push({ name: 'og:title', hid: 'og:title', property: 'og:title', content: title });
  }

  const description = factory.value?.seo_description;
  if (description) {
    head.meta.push({
      hid: 'metadescription',
      name: 'description',
      content: description
    });
    head.meta.push({
      name: 'og:description',
      hid: 'og:description',
      property: 'og:description',
      content: description
    });
  }

  return head;
});

async function getFactoryList () {
  await getFactory();
  if (factory.value && (factory.value.url !== $route.path)) {
    throw createError({
      statusCode: 404,
      statusMessage: 'Фабрика<br/>не найдена'
    });
  }
}

await getFactoryList();
checkFabriky();

await getCharacteristicGroups();
await getCollections();
await getProducts();
</script>

<style scoped lang="scss">
.fabriki-page {
}
.fabriki-page__space {
	margin-top: 124px;
}
.fabriki-page__mobile-content {
  margin-top: 117px;
}
.fabriki-page__mobile-title {
  margin-bottom: 16px;
}
</style>
